import { useEffect, useState } from 'react'
import { useNav } from '../lib/nav'
import * as Sentry from '@sentry/react'
import { Page, TopBar } from '../components/Shell'
import { KV } from '../components/Controls'
import { SignSheet } from '../components/SignSheet'
import { useStore } from '../state/store'
import { createTrade, loadTradeLimits } from '../lib/api'
import { walletLabel, belowFloor, fmtC, lineCents, giveCents, tradeOut, tradeIn, tradeDirection, DIRECTION_LABEL, LABEL, type Employee, type TradeLimits } from '../lib/types'

export interface TradeDoneState { trade_id: string; trade_no: number; sale_id: string; out_total_cents: number; in_total_cents: number; difference_cents: number; direction: string; method: string; by: Employee; approved: Employee | null; skus: string[]; give: string[]; status: string; stock: Record<string, string> | null; order?: string | null }

const METHOD: Record<string, string> = { cash: 'Cash', zelle: 'Zelle', crypto: 'Crypto', paypal: 'PayPal', card: 'Card, charged in Shopify POS', wire: 'Wire', none: '' }

export function TradeReview() {
  const nav = useNav()
  const { trade, customers, resetTrade, refresh } = useStore()
  const [sheet, setSheet] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [limits, setLimits] = useState<TradeLimits | null>(null)
  useEffect(() => { loadTradeLimits().then(setLimits).catch(() => {}) }, [])
  const empty = trade.out.length === 0
  useEffect(() => { if (empty && !busy) nav('/trade', { replace: true }) }, [empty, busy, nav])
  const out = tradeOut(trade), inn = tradeIn(trade), diff = out - inn, dir = tradeDirection(trade)
  const below = trade.out.filter(belowFloor)
  const over = limits ? trade.give.filter((g) => giveCents(g) > limits.staff_credit_limit_cents) : []
  const needsOwner = below.length > 0 || over.length > 0
  const cust = customers.find((c) => c.id === trade.customerId)
  const card = dir === 'customer_pays' && trade.method === 'card'
  const cta = needsOwner ? (card ? 'Owner approves and sends to POS' : 'Owner approves and records') : card ? 'Sign and send to POS' : dir === 'customer_pays' ? 'Sign and collect payment' : 'Sign and complete trade'

  const confirm = async (e: Employee) => {
    setBusy(true); setErr('')
    try {
      const r = await createTrade(trade, e.employee_id, needsOwner ? e.employee_id : null)
      const state: TradeDoneState = { ...r, method: trade.method === 'crypto' ? `Crypto ${walletLabel(trade.coin, trade.network)}` : METHOD[r.settlement_method] ?? r.settlement_method, by: e, approved: needsOwner ? e : null, skus: trade.out.map((l) => l.piece.sku || l.piece.barcode), give: trade.give.map((g) => `${g.brand} ${g.item}`) }
      refresh().catch(() => {})
      nav(r.status === 'awaiting_card' ? '/trade/card' : r.status === 'awaiting_payment' ? `/trade/collect/${r.trade_id}` : '/trade/done', { state, replace: true })
      setTimeout(resetTrade, 0)
    } catch (ex) {
      Sentry.captureException(ex); setErr((ex as Error).message || 'Could not record the trade'); setSheet(false)
    } finally { setTimeout(() => setBusy(false), 50) }
  }

  return (
    <Page bar={<TopBar back="Trade" backTo="/trade" title="Review trade" />}>
      {needsOwner && <div className="banner">{[below.length ? 'A piece is priced below its floor' : '', over.length ? `A credit is over the ${limits ? fmtC(limits.staff_credit_limit_cents) : ''} staff limit` : ''].filter(Boolean).join('. ')}. Shane or Masih has to approve with their ID to finish.</div>}
      <div className="hint">Nothing leaves stock and no intake card prints until the trade is paid.</div>
      {err && <div className="banner" style={{ background: '#d70015' }}>{err}. Nothing was recorded.</div>}
      <div className="section-title">They take</div>
      <div className="card review-rows">
        {trade.out.map((l) => <KV key={l.piece.variant_id} k={l.piece.title} v={<span style={{ color: belowFloor(l) ? '#d70015' : undefined, fontWeight: 600 }}>{fmtC(lineCents(l))}{lineCents(l) < l.piece.list_cents ? ` (list ${fmtC(l.piece.list_cents)})` : ''}{belowFloor(l) ? ' · below floor' : ''}</span>} />)}
      </div>
      <div className="section-title">They give</div>
      <div className="card review-rows">
        {trade.give.map((g) => <KV key={g.key} k={`${g.brand} ${g.item}${g.size ? ' Sz ' + g.size : ''} · ${LABEL.cond[g.cond]} · ${LABEL.whose[g.whose]}'s`} v={<span style={{ fontWeight: 600, color: limits && giveCents(g) > limits.staff_credit_limit_cents ? '#d70015' : undefined }}>{fmtC(giveCents(g))} credit</span>} />)}
      </div>
      <div className="section-title">Settlement</div>
      <div className="card review-rows">
        <KV k="They take" v={fmtC(out)} />
        <KV k="They give" v={'−' + fmtC(inn)} />
        <KV k={DIRECTION_LABEL[dir]} v={dir === 'even' ? 'No money changes hands' : `${fmtC(Math.abs(diff))} by ${trade.method === 'crypto' ? `crypto (${walletLabel(trade.coin, trade.network)})` : METHOD[trade.method] || trade.method}`} bold />
        {card && <KV k="In Shopify POS" v={`Trade-in credit ${fmtC(inn)} as a Custom payment, then card ${fmtC(diff)}`} />}
        <KV k="Customer" v={cust ? `${cust.first_name} ${cust.last_name}`.trim() : 'Not picked'} />
      </div>
      <div className="total"><span>{DIRECTION_LABEL[dir]}</span><span>{dir === 'even' ? 'Even' : fmtC(Math.abs(diff))}</span></div>
      <button type="button" className="cta" onClick={() => setSheet(true)}><span>{cta}</span><span>{dir === 'even' ? '' : fmtC(Math.abs(diff))}</span></button>
      {sheet && <SignSheet title={needsOwner ? 'Owner approval' : 'Sign with your ID'} ownerOnly={needsOwner} onConfirm={confirm} onClose={() => !busy && setSheet(false)} busy={busy} />}
    </Page>
  )
}
